import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const HAND = "'Comic Sans MS', 'Marker Felt', 'Segoe Print', cursive";

// Client-side twin of the parser in routes/breakroom.js — purely so a bad paste is
// caught before a round trip. The server re-parses and is the one that decides.
function parseSpotifyRef(input) {
  const raw = String(input || '').trim();
  if (!raw) return null;

  const uri = raw.match(/^spotify:(playlist|album|track):([A-Za-z0-9]{15,40})$/i);
  if (uri) return { kind: uri[1].toLowerCase(), spotify_id: uri[2] };

  let url;
  try {
    url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  if (!['open.spotify.com', 'play.spotify.com'].includes(url.hostname.toLowerCase().replace(/^www\./, ''))) return null;

  const parts = url.pathname.split('/').filter(Boolean);
  if (parts.length && /^intl-[a-z-]+$/i.test(parts[0])) parts.shift();
  if (parts.length < 2) return null;
  if (!['playlist', 'album', 'track'].includes(parts[0].toLowerCase())) return null;
  if (!/^[A-Za-z0-9]{15,40}$/.test(parts[1])) return null;
  return { kind: parts[0].toLowerCase(), spotify_id: parts[1] };
}

export default function PlaylistWall() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const { data } = await api.get('/breakroom/playlists');
      setItems(data);
    } catch { /* board just stays empty */ }
    setLoaded(true);
  }

  async function share(e) {
    e.preventDefault();
    if (!parseSpotifyRef(url)) {
      setError("That doesn't look like a Spotify link. In Spotify: Share → Copy link.");
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/breakroom/playlists', { url, caption });
      setItems((prev) => [data, ...prev]);
      setUrl('');
      setCaption('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add that one.');
    }
    setBusy(false);
  }

  async function remove(id) {
    try {
      await api.delete(`/breakroom/playlists/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that one.');
    }
  }

  const valid = parseSpotifyRef(url);

  return (
    <div>
      <form onSubmit={share} className="mb-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={url}
            onChange={(e) => { setUrl(e.target.value); setError(''); }}
            placeholder="Paste a Spotify link…"
            className="flex-1 rounded px-3 py-2 text-sm"
            style={{ backgroundColor: '#fffdf5', border: `2px solid ${url && !valid ? '#b3322a' : '#2b2b2b'}`, color: '#1a1a1a' }}
          />
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={140}
            placeholder="Say something about it (optional)"
            className="flex-1 rounded px-3 py-2 text-sm"
            style={{ backgroundColor: '#fffdf5', border: '2px solid #2b2b2b', color: '#1a1a1a', fontFamily: HAND }}
          />
          <button
            type="submit"
            disabled={busy || !valid}
            className="px-4 py-2 rounded font-black uppercase tracking-wide text-xs transition disabled:opacity-40 shrink-0"
            style={{ backgroundColor: '#1db954', color: '#0b3d20', border: '2px solid #2b2b2b' }}
          >
            {busy ? 'Pinning…' : '♪ Pin It'}
          </button>
        </div>
        {error && <p className="text-xs mt-2 font-bold" style={{ color: '#b3322a' }}>{error}</p>}
      </form>

      {loaded && items.length === 0 && (
        <p className="text-center text-sm italic py-6" style={{ color: '#7a6a4a', fontFamily: HAND }}>
          Nothing pinned yet. Somebody has to go first.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => {
          const canDelete = p.author_id === user?.id || user?.role === 'admin';
          return (
            <div key={p.id} className="rounded p-3" style={{ backgroundColor: '#fffdf5', border: '2px solid #2b2b2b' }}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-wide" style={{ color: '#2b2b2b' }}>
                    {p.author_name || 'Someone'}
                    {p.kind !== 'playlist' && (
                      <span className="ml-2 font-bold" style={{ color: '#7a6a4a' }}>· {p.kind}</span>
                    )}
                  </p>
                  {p.caption && (
                    <p className="text-sm mt-0.5 break-words" style={{ color: '#444', fontFamily: HAND }}>“{p.caption}”</p>
                  )}
                </div>
                {canDelete && (
                  <button
                    onClick={() => remove(p.id)}
                    title="Take it down"
                    className="text-lg leading-none shrink-0 transition hover:opacity-60"
                    style={{ color: '#b3322a' }}
                  >
                    &times;
                  </button>
                )}
              </div>
              {/* Rebuilt from the stored kind + ID, never from a pasted URL */}
              <iframe
                title={`${p.kind}-${p.spotify_id}`}
                src={`https://open.spotify.com/embed/${p.kind}/${p.spotify_id}`}
                width="100%"
                height="152"
                frameBorder="0"
                loading="lazy"
                allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                referrerPolicy="no-referrer"
                style={{ borderRadius: 10, display: 'block' }}
              />
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-center mt-4 italic" style={{ color: '#7a6a4a' }}>
        Previews play for anyone. Full songs need you signed into Spotify in this browser.
        Playlists must be public or link-shared, or the player shows up blank for everyone else.
      </p>
    </div>
  );
}
